import { useState, useEffect, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Divider,
} from '@mui/material';
import { useMiseSellerGroups } from '@/lib/query/hooks/useMiseAccounts';
import { useUpdateSellerGroupsNext } from '@/lib/query/hooks/useMiseSellers';
import type { MiseSellerGroup } from '@/lib/types/mise/seller';
import { buildSellerGroupTree } from '@/lib/mise/sellerGroupTree';

interface SellerGroupsNextEditDialogProps {
  open: boolean;
  onClose: () => void;
  accountId: string;
  currentSellerGroupsNext: MiseSellerGroup[] | undefined;
  /** 集計後（current）の店舗グループ。集計前（next）が未設定のときの初期選択フォールバックに使う */
  currentSellerGroups: MiseSellerGroup[] | undefined;
}

export function SellerGroupsNextEditDialog({
  open,
  onClose,
  accountId,
  currentSellerGroupsNext,
  currentSellerGroups,
}: SellerGroupsNextEditDialogProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  // 開くたびに一度だけ初期化するためのフラグ（開いている間の再フェッチによる選択リセットを防ぐ）
  const hasInitializedRef = useRef(false);

  // 店舗グループ一覧を取得（snapshot_hot.seller_groups）
  const {
    data: sellerGroups,
    isLoading: sellerGroupsLoading,
    error: sellerGroupsError,
  } = useMiseSellerGroups(accountId);

  const updateMutation = useUpdateSellerGroupsNext(accountId);

  // 階層フィールドからツリー描画用のフラット配列を構築（pre-order）
  const treeItems = useMemo(() => buildSellerGroupTree(sellerGroups ?? []), [sellerGroups]);

  // 初期値の設定
  // 集計前（next）が設定済みならそれを、未設定（空）なら集計後（current）を初期選択にする。
  // これにより「今登録してある店舗グループ」がチェックON状態でダイアログが開く。
  // 開いている間の再フェッチで選択がリセットされないよう、開くたびに一度だけ初期化する。
  useEffect(() => {
    if (!open) {
      hasInitializedRef.current = false;
      return;
    }
    if (hasInitializedRef.current) return;
    // next が未取得の間は next を優先するため初期化を保留する
    if (currentSellerGroupsNext === undefined) return;
    // next が空なら current にフォールバック。current も未取得なら保留する。
    if (currentSellerGroupsNext.length === 0 && currentSellerGroups === undefined) return;
    const base =
      currentSellerGroupsNext.length > 0 ? currentSellerGroupsNext : (currentSellerGroups ?? []);
    setSelectedIds(new Set(base.map((g) => g.id)));
    hasInitializedRef.current = true;
  }, [open, currentSellerGroupsNext, currentSellerGroups]);

  const handleClose = () => {
    if (!updateMutation.isPending) {
      setConfirmDialogOpen(false);
      onClose();
    }
  };

  const handleToggle = (id: string) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    // 母数は描画ノード集合（重複排除済み・一意）に統一する
    const allSelected = treeItems.length > 0 && treeItems.every((t) => selectedIds.has(t.node.id));
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(treeItems.map((t) => t.node.id)));
    }
  };

  const handleSave = () => {
    setConfirmDialogOpen(true);
  };

  const handleConfirmSave = async () => {
    setConfirmDialogOpen(false);
    try {
      await updateMutation.mutateAsync({
        seller_group_ids: Array.from(selectedIds),
      });
      onClose();
    } catch (error) {
      console.error('Failed to update seller groups next:', error);
    }
  };

  // 全選択/中間状態の判定も描画ノード集合基準に揃える（古い id への堅牢化）
  const isAllSelected = treeItems.length > 0 && treeItems.every((t) => selectedIds.has(t.node.id));
  const isIndeterminate = treeItems.some((t) => selectedIds.has(t.node.id)) && !isAllSelected;

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>集計前の店舗グループを編集</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {sellerGroupsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>店舗グループ一覧を読み込み中...</Typography>
              </Box>
            ) : sellerGroupsError ? (
              <Alert severity="error" sx={{ mt: 1 }}>
                店舗グループ一覧の取得に失敗しました
              </Alert>
            ) : !sellerGroups || sellerGroups.length === 0 ? (
              <Alert severity="info" sx={{ mt: 1 }}>
                店舗グループが登録されていません
              </Alert>
            ) : (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    店舗グループを選択してください（{selectedIds.size}件選択中）
                  </Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isAllSelected}
                        indeterminate={isIndeterminate}
                        onChange={handleSelectAll}
                      />
                    }
                    label={isAllSelected ? '全解除' : '全選択'}
                  />
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Box
                  sx={{
                    maxHeight: 400,
                    overflow: 'auto',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                >
                  {treeItems.map(({ node, depth, isLast, ancestorIsLast }) => (
                    <Box
                      key={node.id}
                      onClick={() => handleToggle(node.id)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        py: 0.5,
                        px: 1,
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: 'action.hover' },
                      }}
                    >
                      {/* コネクタ: proportional フォントでズレないよう等幅 span で描画 */}
                      <Box
                        component="span"
                        sx={{
                          fontFamily: 'monospace',
                          whiteSpace: 'pre',
                          userSelect: 'none',
                          color: 'text.secondary',
                        }}
                      >
                        {/* 祖先の縦ガイド: 最後の子だった祖先の列は空白、それ以外は縦線 */}
                        {ancestorIsLast.map((isLastAncestor, i) => (
                          <span key={i}>{isLastAncestor ? '     ' : '│    '}</span>
                        ))}
                        {/* ルート（depth 0）は記号なし、子孫は └─ / ├─ */}
                        {depth > 0 && <span>{isLast ? '└─' : '├─'}</span>}
                      </Box>
                      <Checkbox
                        checked={selectedIds.has(node.id)}
                        onChange={() => handleToggle(node.id)}
                        onClick={(e) => e.stopPropagation()}
                        size="small"
                      />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" noWrap>
                          {node.name || node.id}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          ID: {node.id}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </>
            )}

            {updateMutation.isError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {updateMutation.error instanceof Error
                  ? updateMutation.error.message
                  : '集計前の店舗グループ一覧の更新に失敗しました'}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={updateMutation.isPending}>
            キャンセル
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={
              updateMutation.isPending ||
              sellerGroupsLoading ||
              !sellerGroups ||
              sellerGroups.length === 0
            }
          >
            {updateMutation.isPending ? '保存中...' : '保存'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-container': {
            zIndex: 1400,
          },
        }}
      >
        <DialogTitle>集計前の店舗グループ一覧を更新しますか？</DialogTitle>
        <DialogContent>
          <Typography>
            選択した{selectedIds.size}件の店舗グループが集計前の店舗グループ一覧に設定されます。
            この操作により、既存の集計前の店舗グループ一覧は上書きされます。よろしいですか？
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)} disabled={updateMutation.isPending}>
            キャンセル
          </Button>
          <Button
            onClick={handleConfirmSave}
            variant="contained"
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending ? '更新中...' : '更新する'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
